import { useQuery } from '@tanstack/react-query';
import {
  loadViewDataQueryFn,
  type ViewDataParams,
} from 'shared/components/CustomTable/hooks/loadViewData';
import {
  type ApiError,
  addResourceNameToQueryResult,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';

export type GetCreditNotesViewParams = ViewDataParams & { view_id: string };

export type CreditNotesView = Awaited<ReturnType<typeof loadViewDataQueryFn>>;

// Per-resource wrapper over the generic views endpoint (loadViewDataQueryFn).
// Replaces the redux-saga `getView` read for the credit-notes list. Forward-
// compatible: when the backend exposes a dedicated `GET /credit_notes` list
// endpoint, only the queryFn here changes — the consumer keeps the same contract.
const emptyCreditNotes: CreditNotesView = { data: [], metadata: undefined };

export function useGetCreditNotesView(params: GetCreditNotesViewParams) {
  const axiosInstance = useLegacyAxiosInstance();
  const { view_id, ...filters } = params;

  const queryResult = useQuery<CreditNotesView, ApiError>({
    queryKey: ['credit-notes-view', params],
    queryFn: () => loadViewDataQueryFn(axiosInstance, view_id, filters),
    enabled: Boolean(view_id),
    placeholderData: previousData => previousData ?? emptyCreditNotes,
  });

  return addResourceNameToQueryResult<CreditNotesView, ApiError, 'creditNotes', 'PreventUndefined'>(
    'creditNotes',
    queryResult,
    emptyCreditNotes,
  );
}
